import * as React from "react";
import { X, Check, Search, ChevronsUpDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Button } from "./button";

export type Option = {
  value: string;
  label: string;
  type?: 'Campaign' | 'Topic';
  icon?: React.ReactNode;
};

interface MultiSelectProps {
  options: Option[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  emptyMessage?: string;
  maxDisplay?: number;
}

// Helper function to format option labels by removing underscores and improving readability
const formatOptionLabel = (label: string): string => {
  // Replace underscores with spaces and capitalize each word
  return label
    .replace(/_/g, ' ')
    .replace(/(\d+)months/, '$1 months')
    .replace(/(\d+)month/, '$1 month')
    .replace(/than month/, 'than a month')
    .replace(/than year/, 'than a year')
    .replace(/\b\w/g, char => char.toUpperCase());
};

// Helper function to get badge colors based on type
const getBadgeColors = (option: Option | undefined): string => {
  if (!option?.type) return "bg-gray-200 text-gray-800 hover:bg-gray-300";
  
  if (option.type === 'Campaign') {
    return "bg-orange-100 text-orange-800 hover:bg-orange-200 border-orange-200";
  } else {
    return "bg-green-100 text-green-800 hover:bg-green-200 border-green-200";
  }
};

export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  ({
    options,
    selected,
    onChange,
    placeholder = "Select options",
    className,
    disabled = false,
    emptyMessage = "No options available",
    maxDisplay = 1
  }, ref) => {
    const [open, setOpen] = React.useState(false);
    const [tempSelected, setTempSelected] = React.useState<string[]>(selected);
    const [searchTerm, setSearchTerm] = React.useState("");

    React.useEffect(() => {
      setTempSelected(Array.isArray(selected) ? [...selected] : []);
    }, [selected]);

    const filteredOptions = options.filter(option => 
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (item: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      setTempSelected(prev => {
        if (prev.includes(item)) {
          return prev.filter(i => i !== item);
        } else {
          return [...prev, item];
        }
      });
    };

    const handleSave = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onChange(tempSelected);
      setOpen(false);
    };

    const handleCancel = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setTempSelected([...selected]);
      setOpen(false);
    };
    
    const handleUnselect = (item: string) => {
      onChange(selected.filter((i) => i !== item));
    };

    return (
      <div className={cn("relative w-full", className)}>
        <Popover open={open && !disabled} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              aria-disabled={disabled}
              className={cn(
                "w-full justify-between min-h-9 h-auto py-1 px-3",
                disabled && "pointer-events-none opacity-50",
                !selected.length && "text-muted-foreground",
                "bg-background border-input text-foreground hover:bg-background hover:border-input transition-colors"
              )}
              onClick={() => setOpen(!open)}
              style={{ 
                position: 'relative', 
                zIndex: 1, 
                pointerEvents: 'auto' 
              }}
            >
              <div className="flex flex-wrap gap-1 items-center">
                {selected.length === 0 ? (
                  <span className="text-muted-foreground">{placeholder}</span>
                ) : (
                  <>
                    {selected.length > maxDisplay ? (
                      <Badge variant="outline" className="rounded-sm px-1 font-normal bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors border-gray-300">
                        {selected.length} selected
                      </Badge>
                    ) : (
                      selected.map((item) => {
                        const option = options.find((option) => option.value === item);
                        const displayText = option ? 
                          (option.label.includes('_') ? formatOptionLabel(option.label) : option.label) : 
                          item;
                        
                        return (
                          <Badge
                            key={item}
                            variant="outline"
                            className={cn(
                              "rounded-sm px-1 font-normal cursor-pointer transition-colors flex items-center gap-1 border-gray-300",
                              getBadgeColors(option)
                            )}
                          >
                            {option?.icon}
                            {displayText}
                            <button
                              className="ml-1 hover:bg-gray-400 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-colors"
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleUnselect(item);
                                }
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleUnselect(item);
                              }}
                              style={{ pointerEvents: 'auto' }}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        );
                      })
                    )}
                  </>
                )}
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            className={cn(
              "w-full min-w-[350px] p-0", 
              "bg-background border-border text-foreground"
            )}
            align="start" 
            sideOffset={5}
            avoidCollisions={true}
            style={{ 
              zIndex: 999999, 
              pointerEvents: 'auto',
              position: 'relative',
            }}
            onInteractOutside={(e) => {
              e.preventDefault();
            }}
          >
            <div className="px-3 py-2 border-b border-border">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search options..."
                  className="pl-8 bg-background border-input text-foreground focus:border-ring transition-colors"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ position: 'relative', zIndex: 1, pointerEvents: 'auto' }}
                />
              </div>
            </div>
            <ScrollArea className="h-72">
              <div className="p-1">
                {filteredOptions.length === 0 ? (
                  <div className="text-sm p-2 text-muted-foreground">{emptyMessage}</div>
                ) : (
                  filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      className={cn(
                        "flex items-center space-x-2 p-2 cursor-pointer rounded-sm transition-colors",
                        tempSelected.includes(option.value) 
                          ? "bg-accent hover:bg-accent/80" 
                          : "hover:bg-accent/50"
                      )}
                      onClick={(e) => handleSelect(option.value, e)}
                      style={{ position: 'relative', zIndex: 99999, pointerEvents: 'auto' }}
                    >
                      <Checkbox
                        checked={tempSelected.includes(option.value)}
                        onCheckedChange={() => {
                          setTempSelected(prev => {
                            if (prev.includes(option.value)) {
                              return prev.filter(i => i !== option.value);
                            } else {
                              return [...prev, option.value];
                            }
                          });
                        }}
                        id={`option-${option.value}`}
                        className="data-[state=checked]:bg-primary data-[state=checked]:border-primary pointer-events-auto"
                        style={{ pointerEvents: 'auto' }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex items-center gap-2">
                        {option.icon}
                        <label 
                          htmlFor={`option-${option.value}`}
                          className="text-sm cursor-pointer flex-grow text-foreground"
                        >
                          {option.label.includes('_') ? formatOptionLabel(option.label) : option.label}
                        </label>
                      </div>
                      {tempSelected.includes(option.value) && (
                        <Check className="h-4 w-4 ml-auto text-primary" />
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
            <div className="flex justify-end gap-2 p-2 border-t border-border">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleCancel}
                className="bg-background hover:bg-accent border-border text-foreground transition-colors"
                style={{ position: 'relative', zIndex: 99999, pointerEvents: 'auto' }}
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button 
                variant="default" 
                size="sm" 
                onClick={handleSave}
                className="bg-primary hover:bg-primary/90 text-primary-foreground transition-colors"
                style={{ position: 'relative', zIndex: 99999, pointerEvents: 'auto' }}
              >
                <Check className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  }
);

MultiSelect.displayName = "MultiSelect";
